/*
 * Copyright (c) 2025. Chad and contributors.
 * All rights reserved.
 */

import {useRouter} from 'vue-router';

export interface NavigationData {
    href: string;
    resource: string;
    title: string;
    target: string;
    icon: string;
}

const STORAGE_KEY = 'navigation_return_data';

/**
 * Store navigation data to localStorage
 */
export const storeNavigationData = (data: NavigationData): void => {
    console.dir('data');
    console.dir(data);
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        // Dispatch custom event for same-tab reactivity
        // The storage event only fires for cross-tab changes, so we need a custom event
        if (typeof window !== 'undefined' && window.dispatchEvent) {
            console.dir('HERE 77788');
            window.dispatchEvent(new CustomEvent('navigation-data-stored'));
        }
    } catch (error) {
        console.error('Failed to store navigation data:', error);
    }
};

/**
 * Get stored navigation data from localStorage
 */
export const getNavigationData = (): NavigationData | null => {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : null;
    } catch (error) {
        console.error('Failed to get navigation data:', error);
        return null;
    }
};

/**
 * Clear-stored navigation data from localStorage
 */
export const clearNavigationData = (): void => {
    console.dir('clearNavigationData ##########');
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('Failed to clear navigation data:', error);
    }
};

/**
 * Helper function to create navigation data for settings pages
 * @param currentPath - Current route path (href)
 * @param title - Title to display in the return link
 * @param targetPath - Target path with optional hash (e.g., '/settings/prices#advanced-pricing')
 * @param resource - Resource type (e.g., 'price_rulesets')
 * @param icon - Icon name (e.g., 'box')
 */
export const createSettingsNavigationData = (
    currentPath: string,
    title: string,
    targetPath: string,
    resource: string = 'settings',
    icon: string = 'settings'
): NavigationData => {
    return {
        href: currentPath,
        resource,
        title,
        target: targetPath,
        icon,
    };
};

/**
 * Composable for handling navigation with return data storage
 */
export function useNavigationHelper() {
    const router = useRouter();

    /**
     * Navigate to a route and store return data
     * @param route - Route name or location object
     * @param data - Navigation data to store
     */
    const navigateWithReturnData = async (
        route: string | { name: string } | { path: string },
        data: NavigationData
    ): Promise<void> => {
        storeNavigationData(data);
        await router.push(route);
    };

    /**
     * Navigate to a settings page with return data (convenience method)
     * @param currentPath - Current route path
     * @param title - Title to display in the return link
     * @param targetRoute - Target route name or location object
     * @param targetPath - Target path with optional hash
     * @param resource - Resource type (optional)
     * @param icon - Icon name (optional)
     */
    const navigateToSettingsWithReturn = async (
        currentPath: string,
        title: string,
        targetRoute: string | { name: string } | { path: string },
        targetPath: string,
        resource: string = 'settings',
        icon: string = 'settings'
    ): Promise<void> => {
        const navigationData = createSettingsNavigationData(
            currentPath,
            title,
            targetPath,
            resource,
            icon
        );
        await navigateWithReturnData(targetRoute, navigationData);
    };

    /**
     * Handle router link click with return data storage
     * This can be used as a click handler for router-link components
     * @param event - Click event
     * @param route - Route name or location object
     * @param data - Navigation data to store
     */
    const handleRouterLinkClick = (
        event: Event,
        route: string | { name: string } | { path: string },
        data: NavigationData
    ): void => {
        event.preventDefault();
        void navigateWithReturnData(route, data);
    };

    return {
        storeNavigationData,
        getNavigationData,
        clearNavigationData,
        navigateWithReturnData,
        navigateToSettingsWithReturn,
        handleRouterLinkClick,
        createSettingsNavigationData,
    };
}
